import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import { saveAuthToCookies, getAuthFromCookies, clearAuthCookies } from '../utils/cookies';

const AuthContext = createContext(null);

// Map of message codes to translation keys
const messageTranslations = {
  'REGISTRATION_SUCCESS': 'messages.registrationSuccess',
  'PASSWORD_CHANGED': 'messages.passwordChanged',
  'EMAIL_VERIFIED': 'messages.emailVerified',
  'EMAIL_SENT': 'messages.emailSent',
  'OK': 'messages.ok',
  'LOGGED_OUT': 'messages.loggedOut'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  // Translate message codes to localized strings
  const translateMessage = useCallback((code) => {
    const translationKey = messageTranslations[code];
    if (translationKey) {
      return t(translationKey);
    }
    return code;
  }, [t]);

  useEffect(() => {
    // Try localStorage first, then fallback to cookies
    let accessToken = localStorage.getItem('accessToken');
    let storedUser = localStorage.getItem('user');

    // If no localStorage data, try cookies
    if (!accessToken) {
      const cookieAuth = getAuthFromCookies();
      if (cookieAuth.accessToken && cookieAuth.refreshToken) {
        // Restore tokens from cookies to localStorage for consistency
        localStorage.setItem('accessToken', cookieAuth.accessToken);
        localStorage.setItem('refreshToken', cookieAuth.refreshToken);
        if (cookieAuth.user) {
          localStorage.setItem('user', JSON.stringify(cookieAuth.user));
          storedUser = JSON.stringify(cookieAuth.user);
        }
        accessToken = cookieAuth.accessToken;
      }
    }

    if (accessToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        // Apply user's preferred language
        if (userData.preferredLanguage) {
          i18n.changeLanguage(userData.preferredLanguage);
          localStorage.setItem('language', userData.preferredLanguage);
        }
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        clearAuthCookies();
      }
    }
    setLoading(false);
  }, [i18n]);

  const saveAuth = useCallback((data) => {
    // Save to localStorage
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    const userData = {
      email: data.email,
      isEmailVerified: data.isEmailVerified,
      preferredLanguage: data.preferredLanguage
    };
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    // Save to cookies for persistent auth
    saveAuthToCookies(data);
  }, []);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    clearAuthCookies();
    setUser(null);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const data = await authService.login(email, password);
      saveAuth(data);

      // Set language from user preferences
      if (data.preferredLanguage) {
        i18n.changeLanguage(data.preferredLanguage);
        localStorage.setItem('language', data.preferredLanguage);
      }

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || t('common.loginError');
      return { success: false, error: message };
    }
  }, [saveAuth, i18n, t]);

  const logout = useCallback(async () => {
    try {
      // Call backend to revoke refresh tokens
      await authService.logout();
    } catch (error) {
      // Ignore errors during logout, still clear local state
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  const forgotPassword = useCallback(async (email) => {
    try {
      const data = await authService.forgotPassword(email);
      return { success: true, message: translateMessage(data.message) };
    } catch (error) {
      const message = error.response?.data?.message || t('common.emailSendError');
      return { success: false, error: message };
    }
  }, [t, translateMessage]);

  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const data = await authService.resetPassword(token, newPassword);
      return { success: true, message: translateMessage(data.message) };
    } catch (error) {
      const message = error.response?.data?.message || t('common.passwordResetError');
      return { success: false, error: message };
    }
  }, [t, translateMessage]);

  const verifyEmail = useCallback(async (token) => {
    try {
      const data = await authService.verifyEmail(token);
      if (user) {
        const updatedUser = { ...user, isEmailVerified: true };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return { success: true, message: translateMessage(data.message) };
    } catch (error) {
      const message = error.response?.data?.message || t('common.emailVerificationError');
      return { success: false, error: message };
    }
  }, [user, t, translateMessage]);

  const updateUserLanguage = useCallback((language) => {
    setUser(prevUser => {
      if (prevUser) {
        const updatedUser = { ...prevUser, preferredLanguage: language };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      }
      return prevUser;
    });
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    updateUserLanguage,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
